import * as React from 'react'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@/src/icons'
import { Select as SelectPrimitive } from '@base-ui/react/select'

import { cn } from '@/registry/reapit/lib/utils'
import { asChildProps } from '@/registry/reapit/lib/as-child-props'

// ---------------------------------------------------------------------------
// Select — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Structural changes:
//   Radix: Root (value/onValueChange) + Portal > Content > Viewport > Item
//   Base UI: Root (value/onValueChange) + Portal > Positioner > Popup > List > Item
//
// Sub-component renames:
//   Content  → Positioner + Popup
//   Viewport → List
//   Label    → GroupLabel
//   ScrollUpButton   → ScrollUpArrow
//   ScrollDownButton → ScrollDownArrow
//
// CSS variable renames:
//   --radix-select-content-available-height → --available-height
//   --radix-select-content-transform-origin → --transform-origin
//   --radix-select-trigger-height           → --anchor-height
//   --radix-select-trigger-width            → --anchor-width
//
// Data attribute changes:
//   data-[state=open/closed] → data-[open] / data-[starting-style] / data-[ending-style]
//   data-[disabled]          → data-[disabled] (same)
//   data-[highlighted]       → data-[highlighted] (Base UI for keyboard/hover focus)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Select Root — Radix API shim
// ---------------------------------------------------------------------------
// Radix Select.Root uses `onValueChange: (value: string) => void`
// Base UI uses `onValueChange: (value: Value | null, eventDetails) => void`
// We accept the Radix-compatible signature and bridge it.
// ---------------------------------------------------------------------------

function Select({
  onValueChange,
  ...props
}: Omit<React.ComponentProps<typeof SelectPrimitive.Root>, 'onValueChange'> & {
  onValueChange?: (value: string) => void
}) {
  const handleValueChange = React.useCallback(
    (value: unknown) => {
      onValueChange?.(String(value ?? ''))
    },
    [onValueChange],
  )

  return (
    <SelectPrimitive.Root data-slot="select" onValueChange={onValueChange ? handleValueChange : undefined} {...props} />
  )
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

// DS Select sizes:
//   default (medium) — h-9  (36px), px-3, text-base (16px)
//   sm      (compact) — h-8 (32px), px-3, text-sm  (14px) — DS Compact select
//   lg               — h-10 (40px), px-3, text-base (16px)
//
// Icon: DS icon-md (size-5, 20px) for the trigger chevron per Principal decision plan/04
// Placeholder: --text-placeholder (neutral-300) per DS colour.text.placeholder
function SelectTrigger({
  className,
  size = 'default',
  children,
  ...props
}: Omit<React.ComponentProps<typeof SelectPrimitive.Trigger>, 'render'> & {
  size?: 'sm' | 'default' | 'lg'
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "flex w-fit items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[placeholder]:text-text-placeholder data-[size=default]:h-9 data-[size=default]:text-base data-[size=sm]:h-8 data-[size=sm]:text-sm data-[size=lg]:h-10 data-[size=lg]:text-base *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon>
        {/* DS: trailing chevron is icon-md (20px) */}
        <ChevronDownIcon className="size-5 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  position = 'item-aligned',
  align = 'center',
  side = 'bottom',
  ...props
}: {
  className?: string
  children?: React.ReactNode
  /** @deprecated Radix compat — Base UI always uses popper positioning */
  position?: 'item-aligned' | 'popper'
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
} & Omit<React.ComponentProps<typeof SelectPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner align={align} side={side}>
        <SelectPrimitive.Popup
          data-slot="select-content"
          className={cn(
            // Base UI: --available-height, --transform-origin, --anchor-width replace Radix CSS vars
            'relative z-50 max-h-[var(--available-height)] min-w-[8rem] origin-[var(--transform-origin)] overflow-x-hidden overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md outline-none transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className={cn('p-1', 'w-full min-w-[var(--anchor-width)] scroll-my-1')}>
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.GroupLabel>) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn('px-2 py-1.5 text-xs text-muted-foreground', className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof SelectPrimitive.Item>, 'render'>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span data-slot="select-item-indicator" className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('pointer-events-none -mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-5" />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-5" />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
